import { MapPin, Building, Edit, TrendingUp, DollarSign, Hash, Star, ShieldCheck, Phone, Mail } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { AgentHeader } from '../../components/layout/AgentHeader';
import { AgentSidebar } from '../../components/layout/AgentSidebar';
import { BottomNav } from '../../components/layout/BottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { agentApi } from '../../api/agent.api';

const businessTypeLabels: Record<string, string> = {
  INDIVIDUAL: 'Individual',
  SHOP: 'Tienda',
  KIOSK: 'Kiosko',
  FRANCHISE: 'Franquicia',
};

const kycLevelLabels: Record<number, string> = {
  1: 'Básico',
  2: 'Intermedio',
  3: 'Completo',
};

function ProfileSkeleton() {
  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-pulse">
      <div className="h-10 bg-gray-200 rounded w-1/3" />
      <div className="h-48 bg-gray-200 rounded-lg" />
      <div className="h-48 bg-gray-200 rounded-lg" />
      <div className="h-32 bg-gray-200 rounded-lg" />
    </div>
  );
}

const AgentProfilePage = () => {
  const { data: profile, isLoading, isError } = useQuery({
    queryKey: ['agentMeProfile'],
    queryFn: agentApi.getAgentProfile,
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <AgentHeader />

      <div className="flex">
        <AgentSidebar />

        <main className="flex-1 p-4 lg:p-6 pb-20 lg:pb-6">
          {isLoading ? (
            <ProfileSkeleton />
          ) : isError ? (
            <div className="max-w-2xl mx-auto text-center py-16">
              <p className="text-gray-600 mb-2">No se pudo cargar el perfil</p>
              <p className="text-sm text-gray-400">Intenta recargar la página</p>
            </div>
          ) : profile ? (
            <div className="max-w-2xl mx-auto space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Mi Perfil</h2>
                  <p className="text-gray-600">Información del agente</p>
                </div>
                <Button variant="outline">
                  <Edit size={20} className="mr-2" />
                  Editar
                </Button>
              </div>

              {/* Identity Card */}
              <Card variant="bordered">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center shrink-0">
                      <span className="text-white font-bold text-3xl">
                        {profile.user.firstName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-gray-900 truncate">
                        {profile.user.fullName}
                      </h3>
                      <p className="text-sm text-gray-500 truncate">{profile.businessName}</p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Hash size={12} />
                          <span>{profile.agentCode}</span>
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          {profile.status}
                        </span>
                        {profile.kycVerified && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            <ShieldCheck size={11} />
                            KYC {kycLevelLabels[profile.kycLevel] ?? `Nivel ${profile.kycLevel}`}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 border-t border-gray-100 pt-4">
                    <div className="flex items-center gap-3">
                      <Mail className="text-gray-400 shrink-0" size={16} />
                      <span className="text-sm text-gray-700">{profile.user.email}</span>
                    </div>
                    {profile.user.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="text-gray-400 shrink-0" size={16} />
                        <span className="text-sm text-gray-700">{profile.user.phone}</span>
                      </div>
                    )}
                    {profile.businessType && (
                      <div className="flex items-center gap-3">
                        <Building className="text-gray-400 shrink-0" size={16} />
                        <span className="text-sm text-gray-700">
                          {businessTypeLabels[profile.businessType] ?? profile.businessType}
                        </span>
                      </div>
                    )}
                    {profile.city && (
                      <div className="flex items-center gap-3">
                        <MapPin className="text-gray-400 shrink-0" size={16} />
                        <span className="text-sm text-gray-700">
                          {[profile.address, profile.city, profile.state]
                            .filter(Boolean)
                            .join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Stats Card */}
              <Card variant="bordered">
                <CardHeader>
                  <CardTitle>Estadísticas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Total Transacciones</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {(profile.stats.totalTransactions ?? 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Volumen Total</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${(profile.stats.totalVolume ?? 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Comisiones Totales</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${(profile.stats.totalCommissions ?? 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Promedio por Tx</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${(profile.stats.avgTransactionSize ?? 0).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Monthly breakdown */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs font-medium text-gray-500 mb-3 uppercase tracking-wide">Este Mes</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <p className="text-lg font-bold text-gray-900">
                          {profile.stats.monthly.transactions}
                        </p>
                        <p className="text-xs text-gray-500">Transacciones</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-gray-900">
                          ${(profile.stats.monthly.volume ?? 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">Volumen</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-green-600">
                          ${(profile.stats.monthly.commissions ?? 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">Comisiones</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Commission & Float */}
              <Card variant="bordered">
                <CardHeader>
                  <CardTitle>Cuenta del Agente</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <DollarSign className="text-purple-600" size={18} />
                        <span className="text-sm font-medium text-gray-700">Tasa de Comisión</span>
                      </div>
                      <span className="text-lg font-bold text-purple-700">{profile.commissionRate}%</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="text-green-600" size={18} />
                        <span className="text-sm font-medium text-gray-700">Float Disponible</span>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-green-700">
                          {(profile.float.currentBalance ?? 0).toLocaleString()} {profile.float.currency}
                        </span>
                        <p className="text-xs text-gray-500">
                          Mín: {profile.float.minBalance.toLocaleString()} · Máx: {profile.float.maxBalance.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {profile.rating != null && (
                      <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Star className="text-yellow-500" size={18} />
                          <span className="text-sm font-medium text-gray-700">Calificación</span>
                        </div>
                        <span className="text-lg font-bold text-yellow-700">
                          {profile.rating} ({profile.reviewCount} reseñas)
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </main>
      </div>

      <BottomNav />
    </div>
  );
};

export default AgentProfilePage;

